import { useMutation } from '@tanstack/react-query'
import { authFetch } from '@/api/client'
import { ApiError, type ProblemDetail } from '@/lib/errors'

export interface RemovedBackground {
  blob: Blob
  /** Object URL for the transparent PNG. Caller must ``URL.revokeObjectURL`` it. */
  url: string
}

/**
 * Background-removal tool: uploads an image as multipart and gets back a
 * transparent PNG. The backend returns raw image bytes (not JSON), so we use
 * ``authFetch`` + ``ApiError`` to keep error semantics consistent with the
 * rest of the app — same approach as the image-generation tool.
 *
 * Caller owns the returned ``url`` lifecycle: revoke it once the image is no
 * longer rendered, otherwise the blob URL leaks.
 */
export function useRemoveBackground() {
  return useMutation({
    mutationFn: async (file: File): Promise<RemovedBackground> => {
      const form = new FormData()
      form.append('file', file)

      // No Content-Type header — the browser sets the multipart boundary.
      const res = await authFetch('/api/v1/tools/background-removal', {
        method: 'POST',
        body: form,
        credentials: 'same-origin',
      })

      if (!res.ok) {
        let problem: ProblemDetail | null = null
        const contentType = res.headers.get('content-type') ?? ''
        if (contentType.includes('application/json') || contentType.includes('application/problem+json')) {
          try {
            const body = (await res.json()) as ProblemDetail
            if (typeof body.title === 'string') problem = body
          } catch {
            /* non-JSON error body — leave as null */
          }
        }
        const message = problem?.detail ?? problem?.title ?? `Falha ao remover fundo (HTTP ${String(res.status)})`
        throw new ApiError(message, res.status, problem, null)
      }

      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      return { blob, url }
    },
  })
}
